import type {
  IOicpCpoConnectorSnapshot,
  IOicpCpoInstallationSnapshot,
  IOicpCpoStationSnapshot,
} from './oicp-cpo-evse.interface';
import type { IOicpChargingNotification } from './oicp-charging-notification.interface';

export namespace OICPGatewayCommunication {
  export type ValueOf<T> = T[keyof T];

  export interface StatusCode {
    Code: string;
    Description: string;
    AdditionalInfo?: string;
  }

  export interface LegacyHashData {
    Function: string;
    Salt?: string;
    Value?: string;
  }

  export interface HashedPin {
    Function: string;
    LegacyHashData?: LegacyHashData;
    Value?: string;
  }

  export interface Identification {
    RFIDMifareFamilyIdentification?: { UID: string };
    QRCodeIdentification?: {
      EvcoID: string;
      HashedPIN?: HashedPin;
      PIN?: string;
    };
    PlugAndChargeIdentification?: { EvcoID: string };
    RemoteIdentification?: { EvcoID: string };
    RFIDIdentification?: {
      EvcoID: string;
      ExpiryDate?: string;
      PrintedNumber?: string;
      RFID?: string;
      UID?: string;
    };
  }

  export interface CommandAcknowledgement {
    Result: boolean;
    StatusCode: StatusCode;
    SessionID?: string;
    CPOPartnerSessionID?: string;
    EMPPartnerSessionID?: string;
  }

  export interface AuthorizeResponse extends CommandAcknowledgement {
    AuthorizationStatus?: string;
  }

  export type OicpBulkActionType = 'fullLoad' | 'update' | 'insert' | 'delete';
  export type OicpPricingReferenceUnit = 'HOUR' | 'KILOWATT_HOUR' | 'MINUTE';
  export type OicpAvailabilityDay =
    | 'Everyday'
    | 'Workdays'
    | 'Weekend'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday'
    | 'Sunday';
  export type OicpAdditionalReferenceType =
    | 'START FEE'
    | 'FIXED FEE'
    | 'PARKING FEE'
    | 'MINIMUM FEE'
    | 'MAXIMUM FEE';

  export interface PagedResponse<TItem> {
    content: TItem[];
    number: number;
    size: number;
    totalElements: number;
    last: boolean;
    totalPages: number;
    first: boolean;
    numberOfElements: number;
    StatusCode: StatusCode;
  }

  export namespace Msp {
    export const CommandAction = {
      AuthorizeRemoteStart: 'authorize-remote-start',
      AuthorizeRemoteStop: 'authorize-remote-stop',
      ReservationStart: 'reservation-start',
      ReservationStop: 'reservation-stop',
      PullEvseData: 'pull-evse-data',
      PullEvseStatus: 'pull-evse-status',
      PullEvseStatusById: 'pull-evse-status-by-id',
      PullEvseStatusByOperatorId: 'pull-evse-status-by-operator-id',
      GetChargeDetailRecords: 'get-charge-detail-records',
      PushAuthenticationData: 'push-authentication-data',
      PullEvsePricing: 'pull-evse-pricing',
      PullPricingProducts: 'pull-pricing-products',
      MarkEvseHot: 'mark-evse-hot',
      UnmarkEvseHot: 'unmark-evse-hot',
    } as const;

    export type CommandAction = ValueOf<typeof CommandAction>;

    /**
     * EMP remote-start payload sent from backend to gateway.
     * Supports both canonical fields and OICP passthrough fields.
     * The gateway mapper decides what it can build and what should pass through.
     */
    export interface AuthorizeRemoteStartRequest {
      // Canonical fields (preferred when the gateway can map them)
      providerId?: string;
      evseId?: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;
      partnerProductId?: string;
      evcoId?: string;
      idTag?: string;
      identification?: Identification;

      // OICP passthrough fields (used when backend already knows exact payload)
      SessionID?: string;
      CPOPartnerSessionID?: string;
      EMPPartnerSessionID?: string;
      ProviderID?: string;
      EvseID?: string;
      Identification?: Identification;
      PartnerProductID?: string;
    }

    /**
     * EMP remote-stop payload sent from backend to gateway.
     * `providerSessionId` is the canonical alias of OICP `SessionID`.
     */
    export interface AuthorizeRemoteStopRequest {
      // Canonical fields
      providerId?: string;
      evseId?: string;
      providerSessionId?: string;
      sessionId?: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;

      // OICP passthrough fields
      SessionID?: string;
      CPOPartnerSessionID?: string;
      EMPPartnerSessionID?: string;
      ProviderID?: string;
      EvseID?: string;
    }

    export type RemoteStartRequest = AuthorizeRemoteStartRequest;
    export type RemoteStopRequest = AuthorizeRemoteStopRequest;
    export type RemoteStartResponse = CommandAcknowledgement;
    export type RemoteStopResponse = CommandAcknowledgement;

    export interface ReservationStartRequest {
      providerId?: string;
      evseId?: string;
      sessionId?: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;
      partnerProductId?: string;
      identification?: Identification;
      duration?: number;

      ProviderID?: string;
      EvseID?: string;
      SessionID?: string;
      CPOPartnerSessionID?: string;
      EMPPartnerSessionID?: string;
      PartnerProductID?: string;
      Identification?: Identification;
      Duration?: number;
    }

    export interface ReservationStopRequest {
      providerId?: string;
      evseId?: string;
      sessionId?: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;

      ProviderID?: string;
      EvseID?: string;
      SessionID?: string;
      CPOPartnerSessionID?: string;
      EMPPartnerSessionID?: string;
    }

    export type ReservationStartResponse = CommandAcknowledgement;
    export type ReservationStopResponse = CommandAcknowledgement;

    export interface ChargeDetailRecord {
      SessionID: string;
      CPOPartnerSessionID?: string;
      EMPPartnerSessionID?: string;
      PartnerProductID?: string;
      EvseID: string;
      Identification: Identification;
      ChargingStart: string;
      ChargingEnd: string;
      SessionStart: string;
      SessionEnd: string;
      MeterValueStart?: number;
      MeterValueEnd?: number;
      MeterValueInBetween?: number[];
      ConsumedEnergy: number;
      SignedMeteringValues?: unknown;
      CalibrationLawVerificationInfo?: unknown;
      HubOperatorID?: string;
      HubProviderID?: string;
    }

    export interface PushEvseStatusRecord {
      EvseID: string;
      EvseStatus: string;
    }

    export interface PushEvseStatusData {
      OperatorID: string;
      OperatorName?: string;
      EvseStatusRecord: PushEvseStatusRecord[];
    }

    export interface PushEvseStatusRequest {
      actionType?: OicpBulkActionType;
      operatorId?: string;
      operatorName?: string;
      evseId?: string;
      evseStatus?: string;
      evseStatusRecords?: PushEvseStatusRecord[];

      ActionType?: OicpBulkActionType;
      OperatorEvseStatus?: PushEvseStatusData;
    }

    export interface PricingProductAvailabilityPeriod {
      begin: string;
      end: string;
    }

    export interface PricingProductAvailabilityTimes {
      on: OicpAvailabilityDay;
      Periods: PricingProductAvailabilityPeriod[];
    }

    export interface PricingProductAdditionalReference {
      AdditionalReference: OicpAdditionalReferenceType;
      AdditionalReferenceUnit: OicpPricingReferenceUnit;
      PricePerAdditionalReferenceUnit: number;
    }

    export interface PricingProductDataRecord {
      ProductID: string;
      ReferenceUnit: OicpPricingReferenceUnit;
      ProductPriceCurrency: string;
      PricePerReferenceUnit: number;
      MaximumProductChargingPower: number;
      IsValid24hours: boolean;
      ProductAvailabilityTimes?: PricingProductAvailabilityTimes[];
      AdditionalReferences?: PricingProductAdditionalReference[];
    }

    export interface PricingProductData {
      OperatorID: string;
      OperatorName?: string;
      ProviderID: string;
      PricingDefaultPrice: number;
      PricingDefaultPriceCurrency: string;
      PricingDefaultReferenceUnit: OicpPricingReferenceUnit;
      PricingProductDataRecords: PricingProductDataRecord[];
    }

    export interface PushPricingProductDataRequest {
      actionType?: OicpBulkActionType;
      operatorId?: string;
      operatorName?: string;
      providerId?: string;
      pricingDefaultPrice?: number;
      pricingDefaultPriceCurrency?: string;
      pricingDefaultReferenceUnit?: OicpPricingReferenceUnit;
      pricingProductDataRecords?: Array<{
        productId?: string;
        referenceUnit?: OicpPricingReferenceUnit;
        productPriceCurrency?: string;
        pricePerReferenceUnit?: number;
        maximumProductChargingPower?: number;
        isValid24hours?: boolean;
        productAvailabilityTimes?: Array<{
          on?: OicpAvailabilityDay;
          periods?: Array<{ begin?: string; end?: string }>;
        }>;
        additionalReferences?: Array<{
          additionalReference?: OicpAdditionalReferenceType;
          additionalReferenceUnit?: OicpPricingReferenceUnit;
          pricePerAdditionalReferenceUnit?: number;
        }>;
      }>;

      ActionType?: OicpBulkActionType;
      PricingProductData?: PricingProductData;
    }

    export interface EvsePricingProduct {
      ProductID: string;
    }

    export interface EvsePricingRecord {
      EvseID: string;
      ProviderID: string;
      EvseIDProductList: EvsePricingProduct[];
    }

    export interface PushEvsePricingRequest {
      actionType?: OicpBulkActionType;
      evsePricing?: Array<{
        evseId?: string;
        providerId?: string;
        productIds?: string[];
        evseIdProductList?: Array<{ productId?: string; ProductID?: string }>;
      }>;

      ActionType?: OicpBulkActionType;
      EVSEPricing?: EvsePricingRecord[];
    }

    export interface GetChargeDetailRecordsRequest {
      // Canonical fields
      cdrForwarded?: boolean;
      from?: string;
      operatorIds?: string[];
      providerId?: string;
      to?: string;
      sessionIds?: string[];

      // OICP passthrough fields
      CDRForwarded?: boolean;
      From?: string;
      OperatorID?: string[];
      ProviderID?: string;
      To?: string;
      SessionID?: string[];
    }

    export type GetChargeDetailRecordsResponse =
      OICPGatewayCommunication.PagedResponse<ChargeDetailRecord>;

    export interface MarkEvseHotRequest {
      evseId: string;
      evseUid: string;
      reason: 'charging-start' | 'session-start';
      sessionId?: string;
    }

    export interface UnmarkEvseHotRequest {
      evseId: string;
      evseUid: string;
      reason: 'charging-end' | 'charging-error' | 'cdr-received' | 'session-end';
      sessionId?: string;
    }

    export type CommandPayloadMap = {
      [CommandAction.AuthorizeRemoteStart]: AuthorizeRemoteStartRequest;
      [CommandAction.AuthorizeRemoteStop]: AuthorizeRemoteStopRequest;
      [CommandAction.ReservationStart]: ReservationStartRequest;
      [CommandAction.ReservationStop]: ReservationStopRequest;
      [CommandAction.PullEvseData]: Record<string, unknown>;
      [CommandAction.PullEvseStatus]: Record<string, unknown>;
      [CommandAction.PullEvseStatusById]: Record<string, unknown>;
      [CommandAction.PullEvseStatusByOperatorId]: Record<string, unknown>;
      [CommandAction.GetChargeDetailRecords]: GetChargeDetailRecordsRequest;
      [CommandAction.PushAuthenticationData]: Record<string, unknown>;
      [CommandAction.PullEvsePricing]: Record<string, unknown>;
      [CommandAction.PullPricingProducts]: Record<string, unknown>;
      [CommandAction.MarkEvseHot]: MarkEvseHotRequest;
      [CommandAction.UnmarkEvseHot]: UnmarkEvseHotRequest;
    };

    export type CommandPayload<T extends CommandAction> = Partial<
      CommandPayloadMap[T]
    >;

    export interface Command<T extends CommandAction = CommandAction> {
      action: T;
      correlationId?: string;
      payload: CommandPayload<T>;
    }

    export interface CommandAck<
      T extends CommandAction = CommandAction,
      R = unknown,
    > {
      action: T;
      ok: boolean;
      response?: R;
      error?: string;
    }
  }

  export namespace Cpo {
    export const CommandAction = {
      AuthorizeStart: 'authorize-start',
      AuthorizeStop: 'authorize-stop',
      ChargeDetailRecord: 'charge-detail-record',
      PushEvseData: 'push-evse-data',
      PushEvseStatus: 'push-evse-status',
      PushPricingProductData: 'push-pricing-product-data',
      PushEvsePricing: 'push-evse-pricing',
      ChargingNotifications: 'charging-notifications',
    } as const;

    export type CommandAction = ValueOf<typeof CommandAction>;

    export type LegacyHashData = OICPGatewayCommunication.LegacyHashData;
    export type HashedPin = OICPGatewayCommunication.HashedPin;
    export type Identification = OICPGatewayCommunication.Identification;
    export type OicpBulkActionType = OICPGatewayCommunication.OicpBulkActionType;
    export type OicpPricingReferenceUnit =
      OICPGatewayCommunication.OicpPricingReferenceUnit;
    export type OicpAvailabilityDay =
      OICPGatewayCommunication.OicpAvailabilityDay;
    export type OicpAdditionalReferenceType =
      OICPGatewayCommunication.OicpAdditionalReferenceType;
    export type CommandStatusCode = OICPGatewayCommunication.StatusCode;
    export type CommandAcknowledgementResponse =
      OICPGatewayCommunication.CommandAcknowledgement;

    export interface ChargeDetailRecordRequest {
      operatorId?: string;
      sessionId: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;
      partnerProductId?: string;
      evseId: string;
      identification: Identification;
      chargingStart: string;
      chargingEnd: string;
      sessionStart: string;
      sessionEnd: string;
      meterValueStart?: number;
      meterValueEnd?: number;
      meterValuesInBetween?: number[];
      consumedEnergy: number;
      signedMeteringValues?: unknown;
      calibrationLawVerificationInfo?: unknown;
      hubOperatorId?: string;
      hubProviderId?: string;
    }

    export type ChargeDetailRecord = ChargeDetailRecordRequest;

    export interface PushEvseStatusRecord {
      evseId: string;
      evseStatus: string;
    }

    export interface PushEvseStatusRequest {
      actionType?: OicpBulkActionType;
      operatorId?: string;
      operatorName?: string;
      evseId?: string;
      evseStatus?: string;
      evseStatusRecords?: PushEvseStatusRecord[];
    }

    export interface PricingProductAvailabilityPeriod {
      begin?: string;
      end?: string;
    }

    export interface PricingProductAvailabilityTimes {
      on?: OicpAvailabilityDay;
      periods?: PricingProductAvailabilityPeriod[];
    }

    export interface PricingProductAdditionalReference {
      additionalReference?: OicpAdditionalReferenceType;
      additionalReferenceUnit?: OicpPricingReferenceUnit;
      pricePerAdditionalReferenceUnit?: number;
    }

    export interface PricingProductDataRecord {
      productId?: string;
      referenceUnit?: OicpPricingReferenceUnit;
      productPriceCurrency?: string;
      pricePerReferenceUnit?: number;
      maximumProductChargingPower?: number;
      isValid24hours?: boolean;
      productAvailabilityTimes?: PricingProductAvailabilityTimes[];
      additionalReferences?: PricingProductAdditionalReference[];
    }

    export interface PushPricingProductDataRequest {
      actionType?: OicpBulkActionType;
      operatorId?: string;
      operatorName?: string;
      providerId?: string;
      pricingDefaultPrice?: number;
      pricingDefaultPriceCurrency?: string;
      pricingDefaultReferenceUnit?: OicpPricingReferenceUnit;
      pricingProductDataRecords?: PricingProductDataRecord[];
    }

    export interface EvsePricingRecord {
      evseId: string;
      providerId: string;
      productIds: string[];
    }

    export interface PushEvsePricingRequest {
      actionType?: OicpBulkActionType;
      operatorId?: string;
      evsePricing?: EvsePricingRecord[];
    }
    export interface ChargingNotificationsRequest
      extends IOicpChargingNotification {
      identification?: Identification;
      partnerProductId?: string;
      sessionId?: string;
    }

    export interface AuthorizeStartRequest {
      stationId?: string;
      operatorId?: string;
      evseId?: string;
      sessionId?: string;
      cpoPartnerSessionId?: string;
      empPartnerSessionId?: string;
      partnerProductId?: string;
      idTag?: string;
      identification?: Identification;
    }

    export interface AuthorizeStopRequest extends AuthorizeStartRequest {}
    export type AuthorizeStartResponse = OICPGatewayCommunication.AuthorizeResponse;
    export type AuthorizeStopResponse = OICPGatewayCommunication.AuthorizeResponse;

    /**
     * Canonical payload for installation updates pushed to the OICP gateway.
     * The gateway is responsible for mapping this to the OICP EVSE data DTOs.
     */
    export interface PushEvseDataRequest {
      installationId: string;
      installation: IOicpCpoInstallationSnapshot;
      installations?: IOicpCpoInstallationSnapshot[];
      stations: IOicpCpoStationSnapshot[];
      connectors: IOicpCpoConnectorSnapshot[];
      actionType?: 'fullLoad' | 'update' | 'insert' | 'delete';
      operatorId?: string;
      operatorName?: string;
      interestedGateway?: string;
      timestamp?: Date;
      source?: string;
    }

    export type CommandPayloadMap = {
      [CommandAction.AuthorizeStart]: AuthorizeStartRequest;
      [CommandAction.AuthorizeStop]: AuthorizeStopRequest;
      [CommandAction.ChargeDetailRecord]: ChargeDetailRecord;
      [CommandAction.PushEvseData]: PushEvseDataRequest;
      [CommandAction.PushEvseStatus]: PushEvseStatusRequest;
      [CommandAction.PushPricingProductData]: PushPricingProductDataRequest;
      [CommandAction.PushEvsePricing]: PushEvsePricingRequest;
      [CommandAction.ChargingNotifications]: ChargingNotificationsRequest;
    };

    export type CommandPayload<T extends CommandAction> = Partial<
      CommandPayloadMap[T]
    >;

    export interface Command<T extends CommandAction = CommandAction> {
      action: T;
      correlationId?: string;
      payload: CommandPayload<T>;
    }

    export interface CommandAck<
      T extends CommandAction = CommandAction,
      R = unknown,
    > {
      action: T;
      ok: boolean;
      response?: R;
      error?: string;
    }
  }

  // Backward-compatible EMP exports
  export const EmpCommandAction = Msp.CommandAction;
  export type EmpCommandAction = ValueOf<typeof EmpCommandAction>;

  export interface EmpOutboundCommand<
    T extends EmpCommandAction = EmpCommandAction,
  > {
    action: T;
    correlationId?: string;
    payload: Msp.CommandPayload<T>;
  }

  export type EmpOutboundCommandResult<
    T extends EmpCommandAction = EmpCommandAction,
    R = unknown,
  > = {
    action: T;
    response: R;
    warnings?: string[];
    notes?: string[];
  };
}
